import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const TOKEN_KEY = "tkh_admin_token";

export const api = axios.create({ baseURL: `${BASE}/api` });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      window.dispatchEvent(new Event("tkh-unauthorized"));
    }
    return Promise.reject(error);
  }
);

// ---- Public (microsite) ----
export const fetchMicrosite = (slug) =>
  api.get(`/microsite/${slug}`).then((r) => r.data);

export const fetchHome = () => api.get(`/home`).then((r) => r.data);

export const trackEvent = (slug, event) =>
  api.post(`/analytics/${slug}/track`, { event }).catch(() => {});

// ---- Sponsor a Bhagavatam (Razorpay) ----
export const getSponsorTotals = (slug) =>
  api.get(`/sponsor/${slug}`).then((r) => r.data);
export const createSponsorOrder = (slug, payload) =>
  api.post(`/sponsor/${slug}/order`, payload).then((r) => r.data);
export const verifySponsorPayment = (slug, payload) =>
  api.post(`/sponsor/${slug}/verify`, payload).then((r) => r.data);

// ---- Bhāgavatam Glimpses ----
export const getGlimpses = () => api.get(`/glimpses`).then((r) => r.data);
export const getGlimpseEntry = () => api.get(`/glimpses/entry`).then((r) => r.data);

// ---- Admin: Bhāgavatam Glimpse editor ----
export const adminGetGlimpseSettings = () =>
  api.get("/admin/glimpse/settings").then((r) => r.data);
export const adminUpdateGlimpseSettings = (payload) =>
  api.put("/admin/glimpse/settings", payload).then((r) => r.data);

export const adminListFeelings = () =>
  api.get("/admin/glimpse/feelings").then((r) => r.data);
export const adminCreateFeeling = (payload) =>
  api.post("/admin/glimpse/feelings", payload).then((r) => r.data);
export const adminUpdateFeeling = (fid, payload) =>
  api.put(`/admin/glimpse/feelings/${fid}`, payload).then((r) => r.data);
export const adminDeleteFeeling = (fid) =>
  api.delete(`/admin/glimpse/feelings/${fid}`).then((r) => r.data);
export const adminReorderFeelings = (ordered_ids) =>
  api.put("/admin/glimpse/feelings/reorder", { ordered_ids }).then((r) => r.data);

export const adminListExperiences = (feelingId) =>
  api
    .get("/admin/glimpse/experiences", { params: { feeling_id: feelingId } })
    .then((r) => r.data);
export const adminGetExperience = (eid) =>
  api.get(`/admin/glimpse/experiences/${eid}`).then((r) => r.data);
export const adminCreateExperience = (payload) =>
  api.post("/admin/glimpse/experiences", payload).then((r) => r.data);
export const adminUpdateExperience = (eid, payload) =>
  api.put(`/admin/glimpse/experiences/${eid}`, payload).then((r) => r.data);
export const adminDeleteExperience = (eid) =>
  api.delete(`/admin/glimpse/experiences/${eid}`).then((r) => r.data);
export const adminDuplicateExperience = (eid) =>
  api.post(`/admin/glimpse/experiences/${eid}/duplicate`).then((r) => r.data);
export const adminReorderExperiences = (ordered_ids) =>
  api.put("/admin/glimpse/experiences/reorder", { ordered_ids }).then((r) => r.data);

export const uploadGlimpseImage = (file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return api.post("/admin/glimpse/upload-image", fd).then((r) => r.data);
};

export const exportGlimpseCsv = (lang = "en") =>
  api
    .get("/admin/glimpse/export", { params: { lang }, responseType: "blob" })
    .then((r) => {
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `glimpses_${lang}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });

export const importGlimpseCsv = (lang, file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return api
    .post("/admin/glimpse/import", fd, { params: { lang } })
    .then((r) => r.data);
};

// ---- Admin ----
export const login = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);

export const changePassword = (current_password, new_password) =>
  api
    .post("/auth/change-password", { current_password, new_password })
    .then((r) => r.data);

export const getConfig = () => api.get("/config").then((r) => r.data);
export const updateConfig = (payload) =>
  api.put("/config", payload).then((r) => r.data);

export const listCampaigns = () =>
  api.get("/temple-campaigns").then((r) => r.data);
export const getCampaign = (id) =>
  api.get(`/temple-campaigns/${id}`).then((r) => r.data);
export const createCampaign = (payload) =>
  api.post("/temple-campaigns", payload).then((r) => r.data);
export const updateCampaign = (id, payload) =>
  api.put(`/temple-campaigns/${id}`, payload).then((r) => r.data);
export const deleteCampaign = (id) =>
  api.delete(`/temple-campaigns/${id}`).then((r) => r.data);
export const getCampaignAnalytics = (id) =>
  api.get(`/temple-campaigns/${id}/analytics`).then((r) => r.data);

export const downloadKit = async (id, slug) => {
  const res = await api.get(`/temple-campaigns/${id}/qr-kit`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = `TakeKrishnaHome-${slug}-CampaignKit.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadTempleKit = async (campaignId, templeSlug) => {
  const res = await api.get(
    `/temple-campaigns/${campaignId}/temples/${templeSlug}/qr-kit`,
    { responseType: "blob" }
  );
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = `TakeKrishnaHome-${templeSlug}-CampaignKit.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ---- Story Builder ----
export const mediaUrl = (path) => `${BASE}/api/media/${path}`;

// Resolves a stored value to a displayable URL: full URLs pass through,
// object-storage paths are proxied via /api/media.
export const resolveMedia = (src) => {
  if (!src) return "";
  if (/^(https?:|data:|blob:)/i.test(src)) return src;
  return mediaUrl(src);
};

export const uploadCampaignImage = (campaignId, file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return api
    .post(`/temple-campaigns/${campaignId}/upload-image`, fd)
    .then((r) => r.data);
};

export const campaignAudioSrc = (c) =>
  c?.audio_path ? mediaUrl(c.audio_path) : c?.audio_url || null;

export const reorderStorySections = (campaignId, orderedIds) =>
  api
    .put(`/campaigns/${campaignId}/story-sections/reorder`, {
      ordered_ids: orderedIds,
    })
    .then((r) => r.data);

export const uploadCampaignAudio = (campaignId, file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return api
    .post(`/temple-campaigns/${campaignId}/audio`, fd)
    .then((r) => r.data);
};

export const clearCampaignAudio = (campaignId) =>
  api.delete(`/temple-campaigns/${campaignId}/audio`).then((r) => r.data);

export const listStorySections = (campaignId) =>
  api.get(`/campaigns/${campaignId}/story-sections`).then((r) => r.data);
export const createStorySection = (campaignId, payload) =>
  api.post(`/campaigns/${campaignId}/story-sections`, payload).then((r) => r.data);
export const getStorySection = (id) =>
  api.get(`/story-sections/${id}`).then((r) => r.data);
export const updateStorySection = (id, payload) =>
  api.put(`/story-sections/${id}`, payload).then((r) => r.data);
export const deleteStorySection = (id) =>
  api.delete(`/story-sections/${id}`).then((r) => r.data);

const CHUNK_SIZE = 5 * 1024 * 1024;

export const uploadSectionVideo = async (sectionId, file, onProgress) => {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  for (let i = 0; i < totalChunks; i++) {
    const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    const fd = new FormData();
    fd.append("index", i);
    fd.append("chunk", blob, "chunk");
    await api.post(`/story-sections/${sectionId}/video/chunk`, fd);
    onProgress && onProgress(Math.round(((i + 1) / totalChunks) * 100));
  }
  const fd = new FormData();
  fd.append("total_chunks", totalChunks);
  fd.append("filename", file.name);
  return api
    .post(`/story-sections/${sectionId}/video/complete`, fd)
    .then((r) => r.data);
};
